import { Store } from 'vuex';
import { Actions, Context } from 'vuex-smart-module';
import { AxiosResponse } from 'axios';
import { DocsTemplates, ResponseStatuses } from '@/enums';
import {
  CatalogProductShared,
  ProductShared,
  ProductsListShared,
} from '@/modules/products/shared/types';
import ProductsStore from '@/modules/products/store';
import { DocsTemplatesService } from '@/services/documents';
import { WarehousesService } from '@/services/warehouses';
import { OrganizationsSharedService } from '@/modules/organizations/shared/services';
import RootState from '@/store/state';
import { Cart } from '../types';
import CartGetters from './getters';
import CartMutations from './mutations';
import CartState from './state';
import { CartService } from '../services';

const cartService = new CartService();
const warehousesService = new WarehousesService();
const organizationsSharedService = new OrganizationsSharedService();

type num_null = number | null;

export default class CartActions extends Actions<
  CartState,
  CartGetters,
  CartMutations,
  CartActions
> {
  // Глобалдьное состояние Vuex (RootState)
  store!: Store<RootState>;
  products_store!: Context<typeof ProductsStore>;

  $init(store: Store<RootState>): void {
    this.products_store = ProductsStore.context(store);
    this.store = store;
  }

  async upd_cart(payload: Cart.CartUpdPayload | Cart.MultiplyDeleteDTO) {
    this.mutations.TOGGLE_LOADING_CART(true);
    this.mutations.UPDATE_LICENSE_DIALOG({ key: 'loading', value: true });

    payload.filters = {
      ...payload.filters,
      search: this.state.filters.search || null,
    };

    try {
      const data = await cartService.upd_cart(payload);

      if (this.state.edit_license_dialog.visible) {
        this.mutations.UPDATE_LICENSE_KEY(data.data.items);
        this.mutations.UPDATE_LICENSE_DIALOG({ key: 'visible', value: false });
      }

      return data;
    } catch (error) {
      console.log(error);
    } finally {
      this.mutations.TOGGLE_LOADING_CART(false);
      this.mutations.UPDATE_LICENSE_DIALOG({ key: 'loading', value: false });
    }
  }

  async add_to_cart(payload: {
    warehouse_id: number | null;
    product: ProductShared | ProductShared[];
  }) {
    this.mutations.TOGGLE_LOADING_CART(true);
    const { product, warehouse_id } = payload;
    const { is_realization_contract } = this.store.state.orders.new_order;

    try {
      const license_key = this.products_store.state.license_key || this.products_store.state.license_key_arr;
      const data = await cartService.send_cart(product, warehouse_id, license_key, is_realization_contract);

      await this.dispatch('set_cart', data);

      this.products_store.mutations.CLEAR_ADD_PRODUCT_DIALOG();

      if (data.data.popup_message !== null && data.data.not_added_product_ids.length && data.data.not_added_product_ids.length > 0) {
        // если добавление товара не доступно по договору реализации
        this.mutations.SET_IS_NOT_ADDED_PRODUCT_MESSAGE(data.data.popup_message);
        this.mutations.SET_IS_NOT_ADDED_PRODUCTS_MODAL(true);
      } else if (data.data.popup_message !== null) {
        this.mutations.TOGGLE_ACCREDITATION_MESSAGE_DIALOG(true);
      } else {
        this.mutations.TOGGLE_ADDED_PROD_TO_CARD(true);
      }

      return data;
    } catch (error) {
      console.log(error);
      return (error as AxiosResponse);
    } finally {
      this.mutations.TOGGLE_LOADING_CART(false);
    }
  }

  handle_delete_dialog() {
    if (this.state.delete_dialog.visible && this.state.delete_dialog.is_last_element) {
      this.mutations.CLEAR_DELETE_DIALOG();
      this.mutations.CLEAR_TOTAL_PRICE();
    }
  }

  async delete_multiple_products_from_cart(payload: Cart.MultiplyDeleteDTO) {
    if (!Array.isArray(payload.items) || payload.items.length === 0) {
      return;
    }

    const products_for_removal = payload.items.map((item) => ({
      id: item.id,
      quantity: 0,
      is_selected: item.is_selected,
    }));

    try {
      const data = (await this.dispatch('upd_cart', {
        items: products_for_removal,
        warehouse_id: payload.warehouse_id,
        project_id: payload.project_id,
      })) as Cart.CartResponse;

      this.dispatch('handle_delete_dialog');

      await this.dispatch('set_cart', data);
    } catch (error) {
      console.log(error);
    }
  }

  async add_to_cart1(payload: {
    warehouse_id: number | null;
    product: CatalogProductShared | CatalogProductShared[];
  }) {
    this.mutations.TOGGLE_LOADING_CART(true);
    const { product, warehouse_id } = payload;
    const { is_realization_contract } = this.store.state.orders.new_order;

    try {
      const license_key = this.products_store.state.license_key || this.products_store.state.license_key_arr;
      const data = await cartService.send_cart1(product, warehouse_id, license_key, is_realization_contract);

      await this.dispatch('set_cart', data);

      this.products_store.mutations.CLEAR_ADD_PRODUCT_DIALOG();

      if (data.data.popup_message !== null && data.data.not_added_product_ids.length && data.data.not_added_product_ids.length > 0) {
        // если добавление товара не доступно по договору реализации
        this.mutations.SET_IS_NOT_ADDED_PRODUCT_MESSAGE(data.data.popup_message);
        this.mutations.SET_IS_NOT_ADDED_PRODUCTS_MODAL(true);
      } else if (data.data.popup_message !== null) {
        this.mutations.TOGGLE_ACCREDITATION_MESSAGE_DIALOG(true);
      } else {
        this.mutations.TOGGLE_ADDED_PROD_TO_CARD(true);
      }

      return data;
    } catch (error) {
      console.log(error);
      return (error as AxiosResponse);
    } finally {
      this.mutations.TOGGLE_LOADING_CART(false);
    }
  }

  async del_from_cart(del_params: {
    id: number;
    warehouse_id: number | null;
    project_id: number | null;
    is_selected: boolean;
  }) {
    try {
      const data = (await this.dispatch('upd_cart', {
        items: { id: del_params.id, quantity: 0, is_selected: del_params.is_selected },
        warehouse_id: del_params.warehouse_id,
        project_id: del_params.project_id,
      })) as Cart.CartResponse;

      this.dispatch('handle_delete_dialog');

      await this.dispatch('set_cart', data);
    } catch (error) {
      console.log(error);
    }
  }

  async upd_item_cart_quantity(payload: {
    product: Cart.UpdateCartDTO;
    warehouse_id: num_null;
    project_id: num_null;
  }) {
    const { product, warehouse_id, project_id } = payload;

    this.mutations.UPDATE_COUTER_LOADING(true);

    try {
      if (Number(product.quantity) === 0) {
        await this.dispatch('del_from_cart', {
          id: product.id,
          warehouse_id,
          project_id,
          is_selected: product.is_selected ?? false,
        });
      } else {
        const data = (await this.dispatch('upd_cart', {
          items: product,
          warehouse_id,
          project_id,
        })) as Cart.CartResponse;

        await this.dispatch('set_cart', data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.mutations.UPDATE_COUTER_LOADING(false);
    }
  }

  async get_cart() {
    this.mutations.TOGGLE_LOADING_CART(true);

    try {
      const payload = {
        filters: this.state.filters,
      };

      const data = await cartService.get_cart(payload);

      this.dispatch('set_cart', data);
    } catch (error) {
      console.log(error);
    } finally {
      this.mutations.TOGGLE_LOADING_CART(false);
    }
  }

  async get_cart_projects() {
    this.mutations.TOGGLE_LOADING_CART(true);

    try {
      const { data } = await cartService.get_cart_projects();

      this.mutations.SET_PROJECTS(data);
    } catch (error) {
      console.log(error);
    } finally {
      this.mutations.TOGGLE_LOADING_CART(false);
    }
  }

  async get_cart_organizations() {
    this.mutations.TOGGLE_LOADING_CART(true);

    try {
      const { data } = await cartService.get_cart_organizations();

      this.mutations.SET_ORGANIZATIONS(data);
    } catch (error) {
      console.log(error);
    } finally {
      this.mutations.TOGGLE_LOADING_CART(false);
    }
  }

  async get_delivery_adresses(id: string | number) {
    try {
      const { data } = await organizationsSharedService.get_delivery_addresses(id);

      this.mutations.SET_DELIVERY_ADDRESSES(data);
    } catch (error) {
      console.log(error);
    }
  }

  async get_delivery_types(id: string | number) {
    try {
      const { data } = await cartService.get_delivery_types(id);

      this.mutations.SET_DELIVERY_TYPES(data);
    } catch (error) {
      console.log(error);
    }
  }

  async get_delline_terminals(id: string | number) {
    try {
      const { data } = await cartService.get_delline_terminals(id);

      this.mutations.SET_DL_TERMINALS(data);
    } catch (error) {
      console.log(error);
    }
  }

  async get_transport_companies(id: string | number) {
    try {
      const { data } = await organizationsSharedService.get_transport_companies(id);

      this.mutations.SET_TRANSPORT_COMPANIES(data);
    } catch (error) {
      console.log(error);
    }
  }

  async get_warehouses(id: string | number) {
    try {
      const { data } = await warehousesService.get_warehouses(id);

      this.mutations.SET_WAREHOUSES(data);
    } catch (error) {
      console.log(error);
    }
  }

  async get_warehouse_balances(payload: Cart.CartUpdPayload) {
    try {
      const data = await cartService.upd_cart(payload);

      await this.dispatch('set_cart', data);
    } catch (error) {
      console.log(error);
    }
  }

  set_cart({ data }: Cart.CartResponse) {
    this.mutations.SET_CART(data.items);
    this.mutations.SET_ITEMS_WITHOUT_ACCREDITATION(data.items_without_accreditation);
    this.mutations.SET_CART_TOTAL(data.total);
    this.mutations.SET_PROJECT_CHOOSEN(data.project_id);
    this.mutations.SET_PREVIOUS_PROJECT(data.project_id);
    this.mutations.SET_ACCREDITATION_POPUP_MESSAGE(data.popup_message);
    this.mutations.SET_NOT_ADDED_PRODUCTS_IDS(data.not_added_product_ids);
    this.mutations.SET_IS_DISABLE_BTN(data.is_placing_order_not_available);
    this.mutations.SET_IS_CASH_REGISTERS_AUTO_ADDITION(data.is_cash_registers_auto_addition);
  }

  clear_cart() {
    localStorage.removeItem(Cart.StoragePrefix);
    this.mutations.SET_CART([]);
  }

  async cart_search(val: string) {
    try {
      const { data } = (await this.products_store.actions.search_products(
        val,
      )) as ProductsListShared;

      this.mutations.SET_CART_SEARCH(data.items);
    } catch (error) {
      console.log(error);
    }
  }

  // upd_payment_type_item

  async upd_payment_or_project(payload: Cart.CartUpdPayload) {
    this.mutations.TOGGLE_LOADING_CART(true);

    try {
      const data = await cartService.upd_cart(payload);

      await this.dispatch('set_cart', data);
    } catch (error) {
      console.log(error);
    } finally {
      this.mutations.TOGGLE_LOADING_CART(false);
    }
  }

  async get_cart_file_template() {
    try {
      const {
        data: { path },
      } = await new DocsTemplatesService().get_document_template(DocsTemplates.CART_PRODUCT);

      this.mutations.SET_DOC_TEMPLATE_LINK(path);
    } catch (error) {
      console.log(error);
    }
  }

  async send_cart_file(file: File) {
    const { is_realization_contract } = this.store.state.orders.new_order;

    try {
      const { code = ResponseStatuses.Success.OK, data } = await cartService.send_cart_file(file, is_realization_contract);

      this.mutations.SET_FILE_UPLOAD_DETAILS(data);

      return code;
    } catch (error) {
      console.log(error);
      return (error as AxiosResponse).status;
    }
  }

  async dl_places_search(val: string) {
    try {
      const { data } = await cartService.dl_places_search(val);

      this.mutations.SET_DL_PLACES(data);
    } catch (error) {
      console.log(error);
    }
  }

  async delete_products() {
    try {
      await cartService.delete_products();

      if (this.state.delete_dialog.visible) {
        this.mutations.CLEAR_DELETE_DIALOG();
        this.mutations.CLEAR_TOTAL_PRICE();
      }

      this.dispatch('clear_cart');
    } catch (error) {
      console.error(error);
    }
  }

  async get_license_keys(id: number) {
    try {
      if (id) {
        const { data } = await cartService.get_license_keys(id, this.state.search_key);

        this.mutations.UPDATE_CURRENT_LICENSE_KEYS(data.items);
      }
    } catch (error) {
      console.error(error);
    }
  }

  async upd_selected_items(payload: Cart.CartUpdPayload) {
    try {
      const data = await this.dispatch('upd_cart', payload) as Cart.CartResponse;

      await this.dispatch('set_cart', data);
    } catch (error) {
      console.error(error);
    }
  }

  async set_realization_contract(is_realization_contract: boolean) {
    const payload = {
      is_realization_contract,
    };

    try {
      return await cartService.set_realization_contract(payload);
    } catch (error: any) {
      console.error(error);

      if (error.status as number === 400) {
        this.state.realization_contract_dialog = true;
        this.state.realization_contract_error_message = error.data.message;
        this.state.cannot_be_shipped_products = error.data.data;
      }

      return (error as AxiosResponse);
    }
  }

  async set_cash_registers_auto_addition(is_cash_registers_auto_addition: boolean): Promise<void> {
    try {
      await cartService.set_cash_registers_auto_addition({ is_cash_registers_auto_addition });
    } catch (error: any) {
      console.error(error);
    }
  }

  async get_catalog_products(filtersEmpty: boolean) {
    this.mutations.SET_CATALOG_PRODUCTS_LOADING(true);

    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.catalog_products_table_options;

      const payload: Cart.CatalogProductRequest = {
        filters: this.state.catalog_products_filters,
        filters_exist: !filtersEmpty,
        pagination: {
          limit: itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      const { data, meta } = await cartService.get_catalog_products(payload);

      this.mutations.SET_CATALOG_PRODUCTS(data.items);

      this.mutations.UPD_CATALOG_PRODUCTS_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_CATALOG_PRODUCTS_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_CATALOG_PRODUCTS_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_CATALOG_PRODUCTS_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_CATALOG_PRODUCTS_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_CATALOG_PRODUCTS_PAGINATION_FIELD({ key: 'limit', value: meta.limit });

      return data.filters;
    } catch (error) {
      console.log(error);
    } finally {
      this.mutations.SET_CATALOG_PRODUCTS_LOADING(false);
    }
  }
}
